import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import './MessageItem.css';

const MessageItem = ({ 
  message, 
  currentUser,
  showHeader,
  onEdit,
  onDelete,
  onDeleteForEveryone,
  onReply,
  onReact,
  onPin,
  onBookmark,
  onOpenThread,
  onViewProfile,
  onMute,
  onBan,
  currentRoom
}) => {
  const [showActions, setShowActions] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(message.content);

  // Safety check for message structure
  if (!message || !message.senderId) {
    return null;
  }

  const isOwn = message.senderId?._id === currentUser._id;
  const canDelete = isOwn || currentUser.isAdmin || ['admin', 'moderator'].includes(currentUser.accountType);
  const canPin = currentUser.isAdmin; // Only admins can pin
  const canModerate = currentUser.isAdmin || ['admin', 'moderator'].includes(currentUser.accountType);

  // Check if message can be edited (15 minutes)
  const canEdit = isOwn && (Date.now() - new Date(message.createdAt).getTime()) < 15 * 60 * 1000;

  // Helper functions for cosmetics
  const getAvatarFrameClass = () => {
    if (!message.senderId?.equippedCosmetics?.avatarFrame) return '';
    const frameId = message.senderId.equippedCosmetics.avatarFrame;
    const frameMap = {
      'frame-neon-purple': 'avatar-frame-neon-purple',
      'frame-gold-elite': 'avatar-frame-gold-elite',
      'frame-rgb-animated': 'avatar-frame-rgb-animated',
      'frame-fire': 'avatar-frame-fire',
      'frame-ice': 'avatar-frame-ice'
    };
    return frameMap[frameId] || '';
  };
  
  const getUsernameEffectClass = () => {
    if (!message.senderId?.equippedCosmetics?.usernameEffect) return '';
    // Return the ID directly as it matches the CSS class name
    return message.senderId.equippedCosmetics.usernameEffect;
  };
  
  const getBadgeIcons = () => {
    const badgeMap = {
      'badge-og-member': '🏆',
      'badge-giveaway-legend': '🎁',
      'badge-social-star': '⭐',
      'badge-collector': '💎',
      'badge-game-master': '🎮'
    };
    
    if (!message.senderId?.equippedCosmetics?.badges || message.senderId.equippedCosmetics.badges.length === 0) return [];
    
    return message.senderId.equippedCosmetics.badges.map(badgeId => badgeMap[badgeId] || '').filter(Boolean);
  };

  const handleEdit = () => {
    if (editContent.trim() === message.content) {
      setIsEditing(false);
      return;
    }
    onEdit(message._id, editContent);
    setIsEditing(false);
  };

  const handleDeleteMsg = () => {
    if (window.confirm('Delete this message?')) {
      onDelete(message._id);
    }
  };

  const handleDeleteForEveryoneMsg = () => {
    const timeElapsed = Date.now() - new Date(message.createdAt).getTime();
    const fiveMinutes = 5 * 60 * 1000;
    
    if (!canModerate && timeElapsed > fiveMinutes) {
      alert('Messages can only be deleted for everyone within 5 minutes of posting');
      return;
    }
    
    if (window.confirm('Delete this message for everyone?')) {
      onDeleteForEveryone(message._id);
    }
  };

  const renderContent = () => {
    if (isEditing) {
      return (
        <div className="message-edit-box">
          <textarea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            className="edit-textarea"
            autoFocus
          />
          <div className="edit-actions">
            <button onClick={handleEdit} className="btn-save">Save</button>
            <button onClick={() => setIsEditing(false)} className="btn-cancel">Cancel</button>
          </div>
        </div>
      );
    }

    return (
      <div className="message-text">
        <ReactMarkdown
          components={{
            code({node, inline, className, children, ...props}) {
              const match = /language-(\w+)/.exec(className || '');
              return !inline && match ? (
                <SyntaxHighlighter
                  style={vscDarkPlus}
                  language={match[1]}
                  PreTag="div"
                  {...props}
                >
                  {String(children).replace(/\n$/, '')}
                </SyntaxHighlighter>
              ) : (
                <code className={className} {...props}>
                  {children}
                </code>
              );
            }
          }}
        >
          {message.content}
        </ReactMarkdown>
        {message.isEdited && (
          <span className="edited-indicator" title={`Edited at ${new Date(message.editedAt).toLocaleString()}`}>
            (edited)
          </span>
        )}
      </div>
    );
  };

  return (
    <div 
      className={`message-item-container ${isOwn ? 'own-message' : ''} ${message.isPinned ? 'pinned-message' : ''} ${message.deleteForEveryone ? 'deleted-message' : ''}`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      data-message-id={message._id}
    >
      {showHeader ? (
        message.senderId.profile?.avatar?.url ? (
          <img 
            src={message.senderId.profile.avatar.url} 
            alt={message.senderId.username}
            className={`message-avatar ${getAvatarFrameClass()}`}
            onClick={() => onViewProfile(message.senderId._id)}
            style={{ cursor: 'pointer' }}
          />
        ) : (
          <div 
            className={`message-avatar avatar-placeholder ${getAvatarFrameClass()}`}
            onClick={() => onViewProfile(message.senderId._id)}
            style={{ cursor: 'pointer' }}
          >
            {message.senderId.username[0].toUpperCase()}
          </div>
        )
      ) : (
        <div className="message-avatar-spacer" />
      )}
      
      <div className="message-content-wrapper">
        {showHeader && (
          <div className="message-header">
            <span 
              className={`message-author ${message.senderId.accountType} ${getUsernameEffectClass()}`}
              onClick={() => onViewProfile(message.senderId._id)}
              style={{ cursor: 'pointer' }}
            >
              {message.senderId.username}
              {message.senderId.accountType === 'admin' && <span className="badge badge-admin">ADMIN</span>}
              {message.senderId.accountType === 'moderator' && <span className="badge badge-mod">MOD</span>}
            </span>
            
            {/* Display Cosmetic Badges */}
            {getBadgeIcons().length > 0 && (
              <span className="cosmetic-badges-chat">
                {getBadgeIcons().map((icon, index) => (
                  <span key={index} className="cosmetic-badge-inline" title="Cosmetic Badge">
                    {icon}
                  </span>
                ))}
              </span>
            )}
            
            {/* User Status */}
            {message.senderId.chatStatus && (
              <span className={`user-status-dot status-${message.senderId.chatStatus}`} 
                    title={message.senderId.customStatus || message.senderId.chatStatus}>
                {message.senderId.chatStatus === 'online' && '🟢'}
                {message.senderId.chatStatus === 'away' && '🟡'}
                {message.senderId.chatStatus === 'busy' && '🔴'}
                {message.senderId.chatStatus === 'dnd' && '⛔'}
              </span>
            )}
            
            <span className="message-time">
              {new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
            
            {message.isPinned && <span className="pinned-icon">📌 Pinned</span>}
          </div>
        )}
        
        {message.replyTo && (
          <div className="message-reply-preview">
            <span className="reply-line"></span>
            <small>
              Replying to <strong>{message.replyTo.senderId?.username || 'Unknown'}</strong>: 
              {message.replyTo.content?.substring(0, 50)}...
            </small>
          </div>
        )}

        {message.deleteForEveryone ? (
          <div className="message-bubble deleted">
            <em>This message was deleted</em>
          </div>
        ) : (
          <>
            <div className="message-bubble">
              {renderContent()}
              
              {/* Link Previews */}
              {message.linkPreviews && message.linkPreviews.length > 0 && (
                <div className="link-previews">
                  {message.linkPreviews.map((preview, idx) => (
                    <a key={idx} href={preview.url} target="_blank" rel="noopener noreferrer" className="link-preview-card">
                      {preview.image && <img src={preview.image} alt={preview.title} className="preview-image" />}
                      <div className="preview-content">
                        <div className="preview-title">{preview.title}</div>
                        <div className="preview-description">{preview.description}</div>
                        <div className="preview-site">{preview.siteName}</div>
                      </div>
                    </a>
                  ))}
                </div>
              )}
              
              {/* File Attachments */}
              {message.attachments && message.attachments.length > 0 && (
                <div className="message-attachments">
                  {message.attachments.map((att, idx) => (
                    <div key={idx} className="attachment-item">
                      {att.type === 'image' ? (
                        <img src={att.url} alt={att.filename} className="attachment-image" />
                      ) : (
                        <a href={att.url} target="_blank" rel="noopener noreferrer" className="attachment-file">
                          📎 {att.filename} ({(att.size / 1024).toFixed(2)} KB)
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Thread Preview */}
            {message.threadReplyCount > 0 && (
              <div className="thread-preview" onClick={() => onOpenThread(message)}>
                💬 {message.threadReplyCount} {message.threadReplyCount === 1 ? 'reply' : 'replies'}
              </div>
            )}
            
            {/* Reactions */}
            {message.reactions && message.reactions.length > 0 && (
              <div className="message-reactions-list">
                {message.reactions.map((r, i) => (
                  <button 
                    key={i} 
                    className={`reaction-chip ${r.users.some(u => u.toString() === currentUser._id.toString()) ? 'active' : ''}`}
                    onClick={() => onReact(message._id, r.emoji)}
                  >
                    {r.emoji} <span className="count">{r.users.length}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Read Receipts */}
            {message.readBy && message.readBy.length > 0 && currentUser.chatPreferences?.showReadReceipts && (
              <div className="read-receipts">
                <small>Read by {message.readBy.length} user{message.readBy.length !== 1 ? 's' : ''}</small>
              </div>
            )}
            
            {/* Action Buttons */}
            {!isEditing && (
              <div className="message-actions-overlay">
                <button 
                  className="menu-dots-btn"
                  onClick={() => setShowMenu(!showMenu)}
                  title="More actions"
                >
                  ⋮
                </button>
                
                {showMenu && (
                  <div className="message-actions-menu">
                    {/* Emoji Picker */}
                    <button 
                      className="menu-item"
                      onClick={() => {
                        setShowEmojiPicker(!showEmojiPicker);
                        setShowMenu(false);
                      }}
                    >
                      😊 Add Reaction
                    </button>
                    
                    {showEmojiPicker && (
                      <div className="emoji-picker-popup">
                        {['👍', '❤️', '😂', '😮', '😢', '🔥', '👏', '🎉'].map(emoji => (
                          <span 
                            key={emoji} 
                            onClick={() => {
                              onReact(message._id, emoji);
                              setShowEmojiPicker(false);
                              setShowMenu(false);
                            }}
                            className="emoji-option"
                          >
                            {emoji}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    <button 
                      className="menu-item"
                      onClick={() => {
                        onReply(message);
                        setShowMenu(false);
                      }}
                    >
                      ↩️ Reply
                    </button>
                    
                    <button 
                      className="menu-item"
                      onClick={() => {
                        onOpenThread(message);
                        setShowMenu(false);
                      }}
                    >
                      💬 Start Thread
                    </button>
                    
                    <button 
                      className="menu-item"
                      onClick={() => {
                        onBookmark(message._id);
                        setShowMenu(false);
                      }}
                    >
                      🔖 Bookmark
                    </button>
                    
                    {canEdit && (
                      <button 
                        className="menu-item"
                        onClick={() => {
                          setIsEditing(true);
                          setShowMenu(false);
                        }}
                      >
                        ✏️ Edit
                      </button>
                    )}
                    
                    {canPin && (
                      <button 
                        className="menu-item"
                        onClick={() => {
                          onPin(message._id);
                          setShowMenu(false);
                        }}
                      >
                        {message.isPinned ? '📌 Unpin' : '📌 Pin'}
                      </button>
                    )}
                    
                    {canDelete && (
                      <>
                        <button 
                          className="menu-item danger"
                          onClick={() => {
                            handleDeleteMsg();
                            setShowMenu(false);
                          }}
                        >
                          🗑️ Delete
                        </button>
                        
                        {canModerate && (
                          <button 
                            className="menu-item danger"
                            onClick={() => {
                              handleDeleteForEveryoneMsg();
                              setShowMenu(false);
                            }}
                          >
                            🗑️ Delete for Everyone
                          </button>
                        )}
                      </>
                    )}
                    
                    {canModerate && !isOwn && (
                      <>
                        <div className="menu-divider"></div>
                        <button 
                          className="menu-item mod-action"
                          onClick={() => {
                            onMute(message.senderId._id, currentRoom._id);
                            setShowMenu(false);
                          }}
                        >
                          🔇 Mute User
                        </button>
                        
                        <button 
                          className="menu-item mod-action"
                          onClick={() => {
                            onBan(message.senderId._id, currentRoom._id);
                            setShowMenu(false);
                          }}
                        >
                          🚫 Ban User
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MessageItem;
